import axios from 'axios'
import { API_BASE_URL } from '../config'

export interface ApiResponse<T = any> {
  data: T
  error?: string
}

export interface RequestOptions {
  skipAuth?: boolean
  skipDefaultErrorHandling?: boolean
  signal?: AbortSignal
  timeout?: number
  retries?: number
  retryDelay?: number
}

export interface MaintenanceSettings {
  enabled: boolean
  message: string
  full_lock?: boolean
}

export interface FeatureFlags {
  email_verification_enabled: boolean
  educational_resources_enabled: boolean
  mobile_app_demo_enabled: boolean
  drug_interactions_enabled: boolean
  billing_subscription_enabled: boolean
  whats_new_enabled: boolean
  ai_assistant_enabled: boolean
  light_mode_disabled: boolean
  visual_preferences_enabled: boolean
  try_demo_enabled: boolean
  prioritize_hardcoded_values: boolean
  pricing_enabled: boolean
  terms_and_conditions_disabled: boolean
  registration_disabled: boolean
  google_signup_disabled: boolean
}

class ApiService {
  private axiosInstance: any

  constructor() {
    if (typeof (axios as any)?.create === 'function') {
      this.axiosInstance = (axios as any).create({
        baseURL: API_BASE_URL,
        withCredentials: true,
        timeout: 30000, // 30 seconds timeout
      })
    } else {
      // Fallback for tests where axios is mocked without create()
      const fallback: any = async (_config: any) => ({ data: {} })
      fallback.defaults = { baseURL: API_BASE_URL }
      fallback.interceptors = { request: { use: () => {} }, response: { use: () => {} } }
      this.axiosInstance = fallback
    }

    this.setupInterceptors()
  }

  private setupInterceptors() {
    // Request interceptor for adding auth token
    this.axiosInstance.interceptors.request.use(
      (config: any) => {
        const token = localStorage.getItem('token')
        if (token && !config.headers.Authorization) {
          config.headers.Authorization = `Bearer ${token}`
        }
        return config
      },
      (error: any) => {
        return Promise.reject(error)
      }
    )

    // Response interceptor for handling common responses
    this.axiosInstance.interceptors.response.use(
      (response: any) => response,
      async (error: any) => {
        const originalRequest = error.config

        // Handle 401 Unauthorized - attempt token refresh
        if (error.response?.status === 401 && !originalRequest._retry) {
          originalRequest._retry = true
          
          // Only attempt token refresh if we're on a page that requires authentication
          const currentPath = window.location.pathname
          const publicPaths = [
            '/',
            '/auth',
            '/play/',  // All game routes
            '/pharmacodynamics',
            '/blood-concentration',
            '/drug-tolerance',  // Read-only drug tolerance information
            '/smart-journal',
            '/smart-medication-journal',
            '/mental-health-companion',
            '/ai-health-assistant',
            '/personalized-precision',
            '/wellness-goal-tracking',
            '/feedback',
            '/conditions',
            '/verify-email',
            '/reset-password',
            '/auth/google/callback',
            '/pricing',
            '/subscription-preview',
            '/chat',
            '/careers',
            '/terms',
            '/puzzle',
            '/about',
            '/maintenance-mode',
            '/mobile-app'
          ]
          
          const isPublicPath = publicPaths.some(path => currentPath.startsWith(path))
          
          if (!isPublicPath) {
            try {
              // Try to refresh token using the refresh endpoint
              // Use a clean axios instance to avoid interceptor conflicts
              const refreshResponse = await axios.post(`${this.axiosInstance.defaults.baseURL}/token/refresh`, null, {
                headers: {
                  Authorization: `Bearer ${localStorage.getItem('token')}`
                }
              })
              
              const newToken = refreshResponse.data.access_token
              if (newToken) {
                localStorage.setItem('token', newToken)
                originalRequest.headers.Authorization = `Bearer ${newToken}`
                return this.axiosInstance(originalRequest)
              } else {
                throw new Error('Token refresh failed')
              }
            } catch (refreshError) {
              // Refresh failed, clear token and redirect
              localStorage.removeItem('token')
              window.location.href = '/auth'
              return Promise.reject(refreshError)
            }
          }
        }

        return Promise.reject(error)
      }
    )
  }

  // Generic request method with retry logic
  private async request<T = any>(
    method: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH',
    endpoint: string,
    data?: any,
    options: RequestOptions & Record<string, any> = {}
  ): Promise<T> {
    const { skipAuth, skipDefaultErrorHandling, signal, retries = 3, retryDelay = 1000, ...axiosConfig } = options
    
    let lastError: any
    
    for (let attempt = 0; attempt <= retries; attempt++) {
      try {
        const config: Record<string, any> = {
          method,
          url: endpoint,
          signal,
          ...axiosConfig,
        }

        if (data) {
          if (method === 'GET') {
            config.params = data
          } else {
            config.data = data
          }
        }

        // Skip auth for specific requests
        if (skipAuth) {
          delete config.headers?.Authorization
        }

        if (options.timeout) {
          config.timeout = options.timeout
        }
        
        const response = await this.axiosInstance(config)
        return response.data
      } catch (error) {
        lastError = error
        
        // Don't retry on certain errors
        if ((error as any)?.response?.status === 401 || (error as any)?.response?.status === 403 || (error as any)?.response?.status === 404) {
          break
        }
        
        // Don't retry on abort signals
        if (signal?.aborted) {
          break
        }
        
        // Don't retry on the last attempt
        if (attempt === retries) {
          break
        }
        
        // Wait before retrying (exponential backoff)
        const delay = retryDelay * Math.pow(2, attempt)
        console.warn(`API request failed (attempt ${attempt + 1}/${retries + 1}), retrying in ${delay}ms:`, (error as any)?.message)
        await new Promise(resolve => setTimeout(resolve, delay))
      }
    }
    
    if (skipDefaultErrorHandling) {
      throw lastError
    }

    // Create a user-friendly error
    const apiError = this.createApiError(lastError)
    throw apiError
  }

  // HTTP method helpers
  async get<T = any>(
    endpoint: string,
    params?: Record<string, any>,
    options: RequestOptions = {}
  ): Promise<T> {
    return this.request<T>('GET', endpoint, params, options)
  }

  async post<T = any>(
    endpoint: string,
    data?: any,
    options: RequestOptions = {}
  ): Promise<T> {
    return this.request<T>('POST', endpoint, data, options)
  }

  async put<T = any>(
    endpoint: string,
    data?: any,
    options: RequestOptions = {}
  ): Promise<T> {
    return this.request<T>('PUT', endpoint, data, options)
  }

  async patch<T = any>(
    endpoint: string,
    data?: any,
    options: RequestOptions = {}
  ): Promise<T> {
    return this.request<T>('PATCH', endpoint, data, options)
  }

  async delete<T = any>(
    endpoint: string,
    data?: any,
    options: RequestOptions = {}
  ): Promise<T> {
    return this.request<T>('DELETE', endpoint, data, options)
  }

  // File upload helper
  async uploadFile<T = any>(
    endpoint: string,
    file: File | FormData,
    options: RequestOptions & { onUploadProgress?: (progress: number) => void } = {}
  ): Promise<T> {
    const { onUploadProgress, ...requestOptions } = options
    
    const formData = file instanceof FormData ? file : new FormData()
    if (file instanceof File) {
      formData.append('file', file)
    }

    return this.request<T>('POST', endpoint, formData, {
      ...requestOptions,
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      onUploadProgress: onUploadProgress ? (progressEvent: any) => {
        const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total!)
        onUploadProgress(progress)
      } : undefined,
    })
  }

  // Download file helper
  async downloadFile(endpoint: string, filename?: string, options: RequestOptions = {}): Promise<void> {
    try {
      // Use the request method to ensure proper authentication handling
      const response = await this.request('GET', endpoint, undefined, {
        ...options,
        responseType: 'blob',
        skipDefaultErrorHandling: true
      })

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response]))
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', filename || 'download')
      document.body.appendChild(link)
      link.click()
      link.remove()
      window.URL.revokeObjectURL(url)
    } catch (error) {
      // Re-throw the error so it can be handled by the calling component
      throw error
    }
  }

  // Error handling helper
  private createApiError(error: unknown): Error {
    if (axios.isAxiosError(error)) {
      const status = error.response?.status
      const rawDetail = (error.response as any)?.data?.detail
      const baseMessage = (error.response as any)?.data?.message
      const message = baseMessage || rawDetail || error.message
      
      // Helper to turn FastAPI/Pydantic validation details into human-readable text
      const formatValidationDetails = (detail: unknown): string => {
        try {
          if (!detail) return 'Invalid input.'

          // FastAPI 422 detail is typically an array of {loc, msg}
          if (Array.isArray(detail)) {
            const parts = detail.map((d: any) => {
              const path = Array.isArray(d?.loc) ? d.loc.filter((x: any) => typeof x === 'string').join('.') : d?.loc
              const msg = d?.msg || d?.message || JSON.stringify(d)
              return path ? `${path}: ${msg}` : String(msg)
            })
            return parts.join('; ')
          }

          // Sometimes it's an object map
          if (typeof detail === 'object') {
            return Object.entries(detail as Record<string, any>)
              .map(([k, v]) => `${k}: ${typeof v === 'string' ? v : JSON.stringify(v)}`)
              .join('; ')
          }

          return String(detail)
        } catch {
          return 'Invalid input.'
        }
      }
      
      // Create specific error messages based on status codes
      switch (status) {
        case 400:
          return new Error(`Bad Request: ${message}`)
        case 401:
          return new Error('Authentication required. Please log in again.')
        case 403:
          // Preserve backend message for better UX (e.g., unverified email on login)
          if (typeof message === 'string' && message.toLowerCase().includes('email not verified')) {
            return new Error(message)
          }
          return new Error('Access forbidden. You do not have permission to perform this action.')
        case 404:
          return new Error('The requested resource was not found.')
        case 409:
          return new Error(`Conflict: ${message}`)
        case 422:
          return new Error(`Validation Error: ${typeof message === 'object' ? formatValidationDetails(message) : message}`)
        case 429:
          return new Error('Too many requests. Please try again later.')
        case 500:
          return new Error('Internal server error. Please try again later.')
        case 502:
        case 503:
        case 504:
          return new Error('Service temporarily unavailable. Please try again later.')
        default:
          return new Error(message || 'An unexpected error occurred.')
      }
    }

    if (error instanceof Error) {
      return error
    }

    return new Error('An unknown error occurred.')
  }

  // Health check method
  async healthCheck(): Promise<{ status: string }> {
    return this.get('/health', undefined, { skipAuth: true })
  }

  // Maintenance settings
  async getMaintenance(): Promise<MaintenanceSettings> {
    return this.get('/maintenance', undefined, { skipAuth: true })
  }

  async updateMaintenance(data: MaintenanceSettings): Promise<MaintenanceSettings> {
    return this.put('/admin/maintenance', data)
  }

  // Feature flags
  async getFeatures(): Promise<FeatureFlags> {
    return this.get('/features', undefined, { skipAuth: true })
  }

  async updateFeatures(data: FeatureFlags): Promise<FeatureFlags> {
    return this.put('/admin/features', data)
  }

  // Method to update base URL if needed
  updateBaseURL(newBaseURL: string) {
    this.axiosInstance.defaults.baseURL = newBaseURL
  }
}

// Create and export singleton instance
export const apiService = new ApiService()

// Export default for easier importing
export default apiService